import React, { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  Plus, 
  Database, 
  Edit3, 
  Trash2, 
  Eye,
  Code,
  Table,
  Play,
  Download,
  Upload
} from 'lucide-react'
import toast from 'react-hot-toast'
import CreateCrudModal from '../../components/modals/CreateCrudModal'
import EditCrudModal from '../../components/modals/EditCrudModal'
import crudService, { CrudEntity, CreateCrudData } from '../../services/crudService'

// Servicio real importado desde services/crudService

const CrudsPage: React.FC = () => {
  const navigate = useNavigate()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedCrud, setSelectedCrud] = useState<CrudEntity | null>(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [crudToEdit, setCrudToEdit] = useState<CrudEntity | null>(null)
  const [crudToDelete, setCrudToDelete] = useState<CrudEntity | null>(null)
  const queryClient = useQueryClient()
  const importInputRef = useRef<HTMLInputElement>(null)
  const [maintCrudId, setMaintCrudId] = useState<string>('')
  const [showCleanupConfirm, setShowCleanupConfirm] = useState(false)
  const [pendingCleanupId, setPendingCleanupId] = useState<number | null>(null)
  const [showGenerateConfirm, setShowGenerateConfirm] = useState(false)
  const [pendingGenerateCrud, setPendingGenerateCrud] = useState<CrudEntity | null>(null)

  const { data: cruds, isLoading, error } = useQuery({
    queryKey: ['cruds'],
    queryFn: () => crudService.getCruds()
  })

  const deleteCrudMutation = useMutation({
    mutationFn: async (id: number | string) => crudService.deleteCrud(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['cruds'])
      toast.success('CRUD eliminado exitosamente')
    },
    onError: () => {
      toast.error('Error al eliminar CRUD')
    }
  })

  const handleEdit = (crud: CrudEntity) => {
    setCrudToEdit(crud)
    setShowEditModal(true)
  }

  const handleExport = (crud: CrudEntity) => {
    try {
      const dataStr = JSON.stringify(crud, null, 2)
      const blob = new Blob([dataStr], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const filename = `${crud.table_name || crud.name}-crud.json`
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      toast.success('Exportación iniciada')
    } catch (e) {
      toast.error('No se pudo exportar el CRUD')
    }
  }
  

  const generateCrudMutation = useMutation({
    mutationFn: async (id: number | string) => crudService.generateCrud(id),
    onSuccess: () => {
      toast.success('CRUD generado exitosamente')
    },
    onError: () => {
      toast.error('Error al generar CRUD')
    }
  })

  const importCrudMutation = useMutation({
    mutationFn: async (data: CreateCrudData) => crudService.createCrud(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['cruds'])
      toast.success('CRUD importado exitosamente')
    },
    onError: () => {
      toast.error('No se pudo importar el CRUD')
    }
  })

  // Mantenimiento: cleanup de artefactos residuales por ID de CRUD
  const cleanupMutation = useMutation({
    mutationFn: async (id: string | number) => crudService.cleanupArtifacts(id),
    onSuccess: () => {
      // Refrescar lista al terminar
      queryClient.invalidateQueries(['cruds'])
    },
  })

  const handleImportClick = () => {
    importInputRef.current?.click()
  }

  const normalizeImportedData = (raw: any): CreateCrudData => {
    const name = raw.name || 'imported_crud'
    const table = raw.table_name || (typeof raw.name === 'string' ? raw.name.toLowerCase().replace(/\s+/g, '_') : 'imported_table')
    const fields = Array.isArray(raw.fields) ? raw.fields : []
    return {
      name,
      table_name: table,
      description: raw.description || '',
      fields: fields.map((f: any) => ({
        name: f.name || '',
        type: f.type || 'string',
        required: Boolean(f.required),
        max_length: typeof f.max_length === 'number' ? f.max_length : undefined,
        default_value: typeof f.default_value === 'string' ? f.default_value : undefined,
        is_primary_key: Boolean(f.is_primary_key),
        is_searchable: f.is_searchable !== undefined ? Boolean(f.is_searchable) : true,
        show_in_list: f.show_in_list !== undefined ? Boolean(f.show_in_list) : true,
        select_options: Array.isArray(f.select_options) ? f.select_options : undefined,
      })),
      has_api: raw.has_api !== undefined ? Boolean(raw.has_api) : true,
      has_frontend: raw.has_frontend !== undefined ? Boolean(raw.has_frontend) : true,
      permissions: raw.permissions || { read: true, create: true, update: true, delete: true },
    }
  }

  const handleImportFile: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      const text = await file.text()
      const raw = JSON.parse(text)
      const data = normalizeImportedData(raw)
      importCrudMutation.mutate(data)
    } catch (err) {
      toast.error('Archivo inválido. Asegúrate de subir un JSON válido.')
    } finally {
      // Permite re-seleccionar el mismo archivo posteriormente
      e.target.value = ''
    }
  }

  const handleDeleteCrud = (crud: CrudEntity) => {
    console.log('handleDeleteCrud llamado para:', crud.name, 'ID:', crud.id)
    console.log('Objeto CRUD completo:', crud)
    // Abrir modal de confirmación personalizado en lugar de window.confirm
    setCrudToDelete(crud)
  }

  const handleGenerateCrud = (crud: CrudEntity) => {
    setPendingGenerateCrud(crud)
    setShowGenerateConfirm(true)
  }

  const getFieldTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      string: 'bg-blue-100 text-blue-800',
      number: 'bg-green-100 text-green-800',
      boolean: 'bg-purple-100 text-purple-800',
      date: 'bg-orange-100 text-orange-800',
      text: 'bg-gray-100 text-gray-800',
      email: 'bg-pink-100 text-pink-800',
      select: 'bg-teal-100 text-teal-800',
    }
    return colors[type] || 'bg-gray-100 text-gray-800'
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error al cargar CRUDs</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de CRUDs</h1>
          <p className="mt-2 text-sm text-gray-600">
            Crea y administra CRUDs dinámicos para tu aplicación
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleImportClick}
            className="btn btn-secondary btn-md"
            disabled={importCrudMutation.isLoading}
          >
            <Upload className="h-5 w-5" />
            Importar
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-primary btn-md"
          >
            <Plus className="h-5 w-5" />
            Nuevo CRUD
          </button>
        </div>
      </div>

      {/* Área de mantenimiento */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Área de mantenimiento</h2>
        <p className="text-sm text-gray-600 mb-4">Ejecuta limpieza de artefactos residuales para un CRUD eliminado o inconsistente.</p>
        <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-end">
          <div className="w-full sm:w-64">
            <label className="block text-sm font-medium text-gray-700 mb-1">ID de CRUD</label>
            <input
              type="text"
              value={maintCrudId}
              onChange={(e) => setMaintCrudId(e.target.value)}
              placeholder="p.ej. 31"
              className="input input-bordered w-full"
            />
          </div>
          <button
            className="btn-blue-flat btn-square btn-compact"
            onClick={async () => {
              const idStr = (maintCrudId || '').trim()
              if (!idStr) {
                toast.error('Ingresa un ID de CRUD primero')
                return
              }
              // Validación numérica: el backend usa ParseIntPipe
              const idNum = Number(idStr)
              if (!Number.isInteger(idNum)) {
                toast.error('El ID debe ser un número entero')
                return
              }
              // Abrir confirmación visible
              setPendingCleanupId(idNum)
              setShowCleanupConfirm(true)
            }}
            disabled={cleanupMutation.isLoading}
          >
            {cleanupMutation.isLoading ? (
              <>
                <span className="spinner w-4 h-4" />
                Ejecutando...
              </>
            ) : (
              <>
                <Play className="h-4 w-4" />
                Ejecutar cleanup
              </>
            )}
          </button>
        </div>
      </div>

      {/* Confirmación de cleanup */}
      {showCleanupConfirm && pendingCleanupId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold mb-2">Confirmar cleanup</h2>
            <p className="text-sm text-gray-600 mb-4">
              ¿Ejecutar cleanup de artefactos para el CRUD {pendingCleanupId}?
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowCleanupConfirm(false); setPendingCleanupId(null) }}
                className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                  if (pendingCleanupId === null) return
                  setShowCleanupConfirm(false)
                  await toast.promise(
                    cleanupMutation.mutateAsync(pendingCleanupId),
                    {
                      loading: `Ejecutando cleanup para CRUD ${pendingCleanupId}...`,
                      success: (data: any) => data?.message ? `Cleanup OK: ${data.message}` : `Cleanup ejecutado correctamente para CRUD ${pendingCleanupId}`,
                      error: (e: any) => `Error al ejecutar cleanup para CRUD ${pendingCleanupId}: ${e?.response?.data?.message || e?.message || 'Desconocido'}`,
                    }
                  )
                  setMaintCrudId('')
                  setPendingCleanupId(null)
                }}
                className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                disabled={cleanupMutation.isLoading}
              >
                {cleanupMutation.isLoading ? 'Ejecutando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmación de generación */}
      {showGenerateConfirm && pendingGenerateCrud && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold mb-2">Confirmar generación</h2>
            <p className="text-sm text-gray-600 mb-4">
              ¿Generar código para el CRUD {pendingGenerateCrud.name}?
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowGenerateConfirm(false); setPendingGenerateCrud(null) }}
                className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (!pendingGenerateCrud) return
                  setShowGenerateConfirm(false)
                  generateCrudMutation.mutate(pendingGenerateCrud.id)
                  setPendingGenerateCrud(null)
                }}
                className="px-4 py-2 rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
                disabled={generateCrudMutation.isLoading}
              >
                {generateCrudMutation.isLoading ? 'Generando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CRUDs Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {isLoading ? (
          <div className="col-span-full flex justify-center py-12">
            <div className="spinner w-8 h-8"></div>
          </div>
        ) : (
          cruds?.map((crud) => (
            <div key={crud.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              {/* CRUD Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <div className="h-12 w-12 bg-primary-100 rounded-lg flex items-center justify-center">
                    <Database className="h-6 w-6 text-primary-600" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-gray-900">{crud.name}</h3>
                    <p className="text-sm text-gray-500">{crud.table_name}</p>
                  </div>
                </div>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  crud.is_active
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {crud.is_active ? 'Activo' : 'Inactivo'}
                </span>
              </div>

              {/* Description */}
              <p className="text-sm text-gray-600 mb-4">{crud.description}</p>

              {/* Fields Summary */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Campos ({crud.fields.length}):
                </h4>
                <div className="space-y-1 max-h-24 overflow-y-auto">
                  {crud.fields.slice(0, 3).map((field) => (
                    <div key={field.name} className="flex items-center justify-between text-xs">
                      <span className="text-gray-600">
                        {field.name}
                        {field.is_primary_key && <span className="text-primary-600 ml-1">(PK)</span>}
                      </span>
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getFieldTypeColor(field.type)}`}>
                        {field.type}
                      </span>
                    </div>
                  ))}
                  {crud.fields.length > 3 && (
                    <div className="text-xs text-gray-400">
                      +{crud.fields.length - 3} campos más
                    </div>
                  )}
                </div>
              </div>

              {/* Features */}
              <div className="mb-4">
                <div className="flex gap-2 mb-2">
                  {crud.has_api && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      <Code className="h-3 w-3 mr-1" />
                      API
                    </span>
                  )}
                  {crud.has_frontend && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <Table className="h-3 w-3 mr-1" />
                      Frontend
                    </span>
                  )}
                </div>
                
                {/* Permissions */}
                <div className="text-xs text-gray-500">
                  Permisos: 
                  {crud.permissions?.read && ' Leer'}
                  {crud.permissions?.create && ' Crear'}
                  {crud.permissions?.update && ' Editar'}
                  {crud.permissions?.delete && ' Eliminar'}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                <div className="text-xs text-gray-500">
                  {new Date(crud.created_at).toLocaleDateString()}
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => navigate(`/cruds/${crud.id}/data`)}
                    className="text-primary-600 hover:text-primary-900 p-1"
                    title="Ir a datos"
                  >
                    <Table className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setSelectedCrud(crud)}
                    className="text-blue-600 hover:text-blue-900 p-1"
                    title="Ver detalles"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleEdit(crud)}
                    className="text-gray-600 hover:text-gray-900 p-1"
                    title="Editar"
                  >
                    <Edit3 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleGenerateCrud(crud)}
                    className="text-green-600 hover:text-green-900 p-1"
                    title="Generar código"
                    disabled={generateCrudMutation.isLoading}
                  >
                    <Play className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleExport(crud)}
                    className="text-indigo-600 hover:text-indigo-900 p-1"
                    title="Exportar"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      console.log('Botón eliminar clickeado')
                      handleDeleteCrud(crud)
                    }}
                    className="text-red-600 hover:text-red-900 p-1"
                    title="Eliminar"
                    type="button"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* CRUD Details Modal */}
      {selectedCrud && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-96 overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">Detalles del CRUD: {selectedCrud.name}</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Info */}
              <div className="space-y-3">
                <h3 className="font-medium text-gray-900">Información Básica</h3>
                <div>
                  <span className="font-medium">Nombre:</span> {selectedCrud.name}
                </div>
                <div>
                  <span className="font-medium">Tabla:</span> {selectedCrud.table_name}
                </div>
                <div>
                  <span className="font-medium">Descripción:</span> {selectedCrud.description}
                </div>
                <div>
                  <span className="font-medium">Estado:</span> {selectedCrud.is_active ? 'Activo' : 'Inactivo'}
                </div>
                <div>
                  <span className="font-medium">Características:</span>
                  <div className="mt-1 flex gap-2">
                    {selectedCrud.has_api && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        API Habilitada
                      </span>
                    )}
                    {selectedCrud.has_frontend && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Frontend Habilitado
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Fields */}
              <div className="space-y-3">
                <h3 className="font-medium text-gray-900">Campos ({selectedCrud.fields.length})</h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {selectedCrud.fields.map((field, index) => (
                    <div key={index} className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm">{field.name}</span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getFieldTypeColor(field.type)}`}>
                          {field.type}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 space-x-2">
                        {field.required && <span>Requerido</span>}
                        {field.is_primary_key && <span>Clave Primaria</span>}
                        {field.is_searchable && <span>Buscable</span>}
                        {field.show_in_list && <span>En Lista</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-500">
                Creado: {new Date(selectedCrud.created_at).toLocaleString()} | 
                Actualizado: {new Date(selectedCrud.updated_at).toLocaleString()}
              </div>
            </div>

            <button
              onClick={() => setSelectedCrud(null)}
              className="btn btn-secondary w-full mt-6"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {crudToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold mb-2">Confirmar eliminación</h2>
            <p className="text-sm text-gray-600 mb-4">
              ¿Estás seguro de eliminar el CRUD {crudToDelete.name}? Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  console.log('Usuario canceló la eliminación (modal)')
                  setCrudToDelete(null)
                }}
                className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (!crudToDelete) return
                  console.log('Usuario confirmó eliminación (modal), ejecutando mutación...')
                  deleteCrudMutation.mutate(crudToDelete.id)
                  setCrudToDelete(null)
                }}
                className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create CRUD Modal */}
      <CreateCrudModal 
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />

      {/* Edit CRUD Modal */}
      <EditCrudModal
        isOpen={showEditModal}
        onClose={() => { setShowEditModal(false); setCrudToEdit(null) }}
        crud={crudToEdit}
      />

      {/* Hidden Import Input */}
      <input
        ref={importInputRef}
        type="file"
        accept="application/json"
        className="hidden"
        onChange={handleImportFile}
      />
    </div>
  )
}

export default CrudsPage
